import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatINR, formatDate, inputCls, labelCls, btnPrimary, exportCSV } from "@/lib/constants";
import { Modal } from "@/components/Modal";
import { useAuth, useCanAdmin } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2, Download, Settings, Wallet, Pencil, Eye } from "lucide-react";

const METHODS = [
  ["percent", "Fixed %"],
  ["amount", "Fixed Amount"],
  ["volume_slab", "Volume Slab"],
  ["count_slab", "Number Slab"],
  ["irr_tenure_matrix", "IRR/Tenure Matrix"],
];
const methodLabel = (m) => METHODS.find((x) => x[0] === m)?.[1] || "Fixed %";
const emptyCfg = { lender_id: "", loan_type: "", method: "percent", payout_percent: "", payout_amount: "", tds_percent: "", partner_id: "", slabs: [], effective_from: "", effective_to: "" };

export default function Commissions() {
  const { user } = useAuth();
  const canAdmin = useCanAdmin();
  const isAdmin = user?.role === "admin";
  const canEdit = canAdmin("payout-setup") || canAdmin("commissions");
  const isPartnerStaff = ["dsa", "master_dsa", "agent"].includes(user?.role);
  const [tab, setTab] = useState("payouts");
  const [commissions, setCommissions] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [showCfg, setShowCfg] = useState(false);
  const [editCfgId, setEditCfgId] = useState(null);
  const [viewCfg, setViewCfg] = useState(null);
  const [settleFor, setSettleFor] = useState(null);
  const [settleNote, setSettleNote] = useState("");
  const [settlePaid, setSettlePaid] = useState("");
  const [settleStatus, setSettleStatus] = useState("settled");
  const [editValFor, setEditValFor] = useState(null);
  const [editVal, setEditVal] = useState({ payout_percent: "", incentive: "", tds_percent: "" });
  const [showManual, setShowManual] = useState(false);
  const [manualForm, setManualForm] = useState({ partner_id: "", lender_id: "", loan_type: "", amount: "", note: "" });
  const [cfgForm, setCfgForm] = useState(emptyCfg);
  const [globalTds, setGlobalTds] = useState(5);

  const load = useCallback(() => {
    api.get("/commissions").then((r) => setCommissions(r.data)).catch(() => {});
    api.get("/commission-configs").then((r) => setConfigs(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    api.get("/lenders").then((r) => setLenders(r.data));
    api.get("/partners").then((r) => setPartners(r.data));
    api.get("/products").then((r) => setProducts(r.data.filter((p) => p.active)));
    api.get("/settings/commission").then((r) => setGlobalTds(r.data.default_tds_percent ?? 5)).catch(() => {});
  }, [load]);

  const openCfg = () => { setEditCfgId(null); setCfgForm({ ...emptyCfg, loan_type: products[0]?.name || "" }); setShowCfg(true); };
  const openEditCfg = (c) => {
    setEditCfgId(c.id);
    setCfgForm({
      lender_id: c.lender_id || "", loan_type: c.loan_type || "", method: c.method || "percent",
      payout_percent: c.payout_percent ?? "", payout_amount: c.payout_amount ?? "",
      tds_percent: c.tds_percent ?? "", partner_id: c.partner_id || "",
      slabs: c.slabs || [], effective_from: c.effective_from || "", effective_to: c.effective_to || "",
    });
    setShowCfg(true);
  };

  const saveTds = async () => {
    try {
      const { data } = await api.put("/settings/commission", { default_tds_percent: parseFloat(globalTds) || 0 });
      setGlobalTds(data.default_tds_percent); toast.success("Default TDS updated");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const addSlab = () => setCfgForm((f) => ({ ...f, slabs: [...(f.slabs || []), f.method === "irr_tenure_matrix" ? { min_irr: "", max_irr: "", min_tenure: "", max_tenure: "", payout_percent: "" } : { min: "", max: "", payout_percent: "" }] }));
  const updSlab = (i, k, v) => setCfgForm((f) => ({ ...f, slabs: f.slabs.map((s, idx) => idx === i ? { ...s, [k]: v } : s) }));
  const removeSlab = (i) => setCfgForm((f) => ({ ...f, slabs: f.slabs.filter((_, idx) => idx !== i) }));

  const openSettle = (c) => {
    setSettleFor(c);
    setSettleNote(c.settle_note || "");
    setSettlePaid(c.paid_amount != null ? c.paid_amount : c.net_payout);
    setSettleStatus(c.status === "settled" ? "settled" : "settled");
  };
  const settle = async () => {
    try {
      await api.patch(`/commissions/${settleFor.id}/settle`, {
        note: settleNote,
        paid_amount: settlePaid === "" ? null : parseFloat(settlePaid),
        status: settleStatus,
      });
      toast.success(settleStatus === "settled" ? "Settlement saved" : "Marked as pending");
      setSettleFor(null); setSettleNote(""); setSettlePaid(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const approve = async (c, val) => {
    try {
      await api.patch(`/commissions/${c.id}/approve`, { approved: val });
      toast.success(val ? "Commission approved" : "Approval revoked");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const openEditValues = (c) => {
    setEditValFor(c);
    setEditVal({ payout_percent: c.payout_percent ?? "", incentive: c.incentive ?? 0, tds_percent: c.tds_percent ?? "" });
  };
  const saveEditValues = async () => {
    try {
      await api.put(`/commissions/${editValFor.id}`, {
        payout_percent: editVal.payout_percent === "" ? null : parseFloat(editVal.payout_percent),
        incentive: editVal.incentive === "" ? null : parseFloat(editVal.incentive),
        tds_percent: editVal.tds_percent === "" ? null : parseFloat(editVal.tds_percent),
      });
      toast.success("Commission values updated");
      setEditValFor(null); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const previewNet = () => {
    if (!editValFor) return { gross: 0, tds: 0, net: 0 };
    const pct = parseFloat(editVal.payout_percent) || 0;
    const inc = parseFloat(editVal.incentive) || 0;
    const tdsP = parseFloat(editVal.tds_percent) || 0;
    const gross = Math.round(editValFor.disbursed_amount * pct / 100 * 100) / 100;
    const tds = Math.round((gross + inc) * tdsP / 100 * 100) / 100;
    const net = Math.round((gross + inc - tds) * 100) / 100;
    return { gross, tds, net };
  };

  const submitManual = async (e) => {
    e.preventDefault();
    if (!manualForm.partner_id) { toast.error("Select a partner"); return; }
    if (!(parseFloat(manualForm.amount) > 0)) { toast.error("Enter an amount greater than 0"); return; }
    try {
      await api.post("/commissions/manual", {
        partner_id: manualForm.partner_id, lender_id: manualForm.lender_id || null,
        loan_type: manualForm.loan_type || "", amount: parseFloat(manualForm.amount), note: manualForm.note,
      });
      toast.success("Manual payout added");
      setShowManual(false); setManualForm({ partner_id: "", lender_id: "", loan_type: "", amount: "", note: "" }); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const submitCfg = async (e) => {
    e.preventDefault();
    const payload = {
      lender_id: cfgForm.lender_id, loan_type: cfgForm.loan_type, method: cfgForm.method,
      tds_percent: cfgForm.tds_percent === "" ? null : parseFloat(cfgForm.tds_percent), partner_id: cfgForm.partner_id || null,
      payout_percent: 0, payout_amount: 0, slabs: [],
      effective_from: cfgForm.effective_from || "", effective_to: cfgForm.effective_to || "",
    };
    if (cfgForm.effective_from && cfgForm.effective_to && cfgForm.effective_to < cfgForm.effective_from) {
      toast.error("Effective end date must be after the start date"); return;
    }
    if (cfgForm.method === "percent") payload.payout_percent = parseFloat(cfgForm.payout_percent) || 0;
    else if (cfgForm.method === "amount") payload.payout_amount = parseFloat(cfgForm.payout_amount) || 0;
    else if (cfgForm.method === "irr_tenure_matrix") {
      payload.payout_percent = parseFloat(cfgForm.payout_percent) || 0;
      payload.slabs = (cfgForm.slabs || []).map((s) => ({
        min_irr: s.min_irr === "" ? "" : Number(s.min_irr), max_irr: s.max_irr === "" ? "" : Number(s.max_irr),
        min_tenure: s.min_tenure === "" ? "" : Number(s.min_tenure), max_tenure: s.max_tenure === "" ? "" : Number(s.max_tenure),
        payout_percent: Number(s.payout_percent) || 0,
      }));
    }
    else payload.slabs = (cfgForm.slabs || []).map((s) => ({ min: Number(s.min) || 0, max: s.max === "" ? "" : Number(s.max), payout_percent: Number(s.payout_percent) || 0 }));
    try {
      if (editCfgId) await api.put(`/commission-configs/${editCfgId}`, payload);
      else await api.post("/commission-configs", payload);
      toast.success(editCfgId ? "Payout rule updated" : "Payout rule added");
      setShowCfg(false); setEditCfgId(null); setCfgForm(emptyCfg); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const delCfg = async (id) => { await api.delete(`/commission-configs/${id}`); toast.success("Rule removed"); load(); };

  const cfgValue = (c) => {
    if (c.method === "amount") return formatINR(c.payout_amount);
    if (c.method === "volume_slab" || c.method === "count_slab" || c.method === "irr_tenure_matrix") return `${(c.slabs || []).length} slab(s)`;
    return `${c.payout_percent}%`;
  };

  const totalPending = commissions.filter((c) => c.status === "pending").reduce((s, c) => s + c.net_payout, 0);
  const totalSettled = commissions.filter((c) => c.status === "settled").reduce((s, c) => s + c.net_payout, 0);
  const isSlab = cfgForm.method === "volume_slab" || cfgForm.method === "count_slab";
  const isMatrix = cfgForm.method === "irr_tenure_matrix";

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 font-heading tracking-tight">Commissions</h1>
          <p className="text-sm text-slate-500 mt-1">Auto-calculated payouts, TDS &amp; settlements</p>
        </div>
        {tab === "config" && canEdit && <button data-testid="add-config-button" onClick={openCfg} className={btnPrimary}><Plus size={16} /> Add Payout Rule</button>}
        {tab === "payouts" && (
          <div className="flex gap-2">
            {canEdit && <button data-testid="manual-payout-button" onClick={() => setShowManual(true)} className={btnPrimary}><Plus size={16} /> Manual Payout</button>}
            <button data-testid="export-payout-button" onClick={() => exportCSV(commissions, "commissions.csv")} className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-medium text-sm flex items-center gap-2"><Download size={16} /> Export CSV</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm"><div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-2"><Wallet size={14} /> Pending Payout</div><div className="text-2xl font-extrabold text-amber-600 font-heading mt-1">{formatINR(totalPending)}</div></div>
        <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm"><div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-2"><CheckCircle2 size={14} /> Settled</div><div className="text-2xl font-extrabold text-emerald-600 font-heading mt-1">{formatINR(totalSettled)}</div></div>
        <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm"><div className="text-xs uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-2"><Settings size={14} /> Active Rules</div><div className="text-2xl font-extrabold text-slate-900 font-heading mt-1">{configs.length}</div></div>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        {(isPartnerStaff ? [["payouts", "Settlement Payouts"]] : [["payouts", "Settlement Payouts"], ["config", "Payout Setup"]]).map(([k, l]) => (
          <button key={k} data-testid={`commission-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab === k ? "border-emerald-500 text-emerald-600" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>

      {tab === "payouts" && (
        <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm overflow-x-auto">
          <table className="w-full min-w-[980px]">
            <thead>
              <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left px-4 py-3">Customer</th><th className="text-left px-4 py-3">Partner</th><th className="text-left px-4 py-3">Lender</th>
                <th className="text-right px-4 py-3">Disbursed</th><th className="text-right px-4 py-3">Rate</th><th className="text-right px-4 py-3">Gross</th>
                <th className="text-right px-4 py-3">TDS</th><th className="text-right px-4 py-3">Net Payout</th><th className="text-left px-4 py-3">Status</th>{!isPartnerStaff && <th className="text-right px-4 py-3">Action</th>}
              </tr>
            </thead>
            <tbody>
              {commissions.map((c) => (
                <tr key={c.id} data-testid={`commission-row-${c.id}`} className="border-b border-slate-100 hover:bg-slate-50/80 text-sm text-slate-700">
                  <td className="px-4 py-3 font-medium text-slate-900">{c.customer_name}</td>
                  <td className="px-4 py-3">{c.partner_name || "—"}</td>
                  <td className="px-4 py-3">{c.lender_name}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatINR(c.disbursed_amount)}</td>
                  <td className="px-4 py-3 text-right font-mono">{c.payout_percent}%</td>
                  <td className="px-4 py-3 text-right font-mono">{formatINR(c.gross_commission)}</td>
                  <td className="px-4 py-3 text-right font-mono text-rose-600">-{formatINR(c.tds_amount)}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-600">{formatINR(c.net_payout)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${c.status === "settled" ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{c.status}</span>
                    <div className="mt-0.5">
                      {c.approved
                        ? <span data-testid={`approved-badge-${c.id}`} className="text-[11px] text-emerald-600 font-medium">✓ Approved</span>
                        : <span data-testid={`unapproved-badge-${c.id}`} className="text-[11px] text-slate-400 font-medium">Awaiting approval</span>}
                    </div>
                    {c.status === "settled" && c.paid_amount != null && c.paid_amount !== c.net_payout && <div className="text-[11px] text-slate-400 mt-0.5">Paid {formatINR(c.paid_amount)}</div>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!isPartnerStaff && (
                    <div className="flex items-center justify-end gap-1.5">
                      {isAdmin && !c.manual && c.status !== "settled" && <button data-testid={`edit-values-${c.id}`} title="Edit calculated values" onClick={() => openEditValues(c)} className="px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium inline-flex items-center gap-1"><Pencil size={13} /> Values</button>}
                      {canEdit && !c.approved && <button data-testid={`approve-commission-${c.id}`} onClick={() => approve(c, true)} className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium">Approve</button>}
                      {canEdit && c.approved && c.status !== "settled" && <button data-testid={`unapprove-commission-${c.id}`} onClick={() => approve(c, false)} className="px-2.5 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-medium">Revoke</button>}
                      {canEdit && c.approved && (c.status === "pending"
                        ? <button data-testid={`settle-commission-${c.id}`} onClick={() => openSettle(c)} className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium">Settle</button>
                        : <button data-testid={`edit-settlement-${c.id}`} onClick={() => openSettle(c)} className="px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-medium inline-flex items-center gap-1"><Pencil size={13} /> Edit</button>)}
                    </div>
                    )}
                  </td>
                </tr>
              ))}
              {commissions.length === 0 && <tr><td colSpan={isPartnerStaff ? 9 : 10} className="px-4 py-12 text-center text-slate-400 text-sm">No commissions yet. Disburse a loan to generate payouts.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "config" && (
        <div className="space-y-4">
          <div className="p-4 rounded-xl bg-white border border-slate-200 shadow-sm flex flex-col sm:flex-row sm:items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[200px] max-w-xs">
              <label className={labelCls}>Default TDS % (used in net payout)</label>
              <input data-testid="global-tds-input" type="number" step="0.01" value={globalTds} disabled={!canEdit} onChange={(e) => setGlobalTds(e.target.value)} className={`${inputCls} disabled:bg-slate-50`} />
            </div>
            {canEdit && <button data-testid="save-tds-button" onClick={saveTds} className={btnPrimary}>Save TDS</button>}
            <p className="text-xs text-slate-400 flex-1 min-w-[180px]">Applied to every payout unless a rule sets its own TDS.</p>
          </div>
          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm overflow-x-auto">
            <table className="w-full min-w-[760px]">
            <thead>
              <tr className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="text-left px-4 py-3">Lender</th><th className="text-left px-4 py-3">Loan Type</th><th className="text-left px-4 py-3">Method</th><th className="text-left px-4 py-3">Scope</th>
                <th className="text-right px-4 py-3">Payout</th><th className="text-right px-4 py-3">TDS %</th><th className="text-left px-4 py-3">Effective</th><th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((c) => (
                <tr key={c.id} data-testid={`config-row-${c.id}`} className="border-b border-slate-100 hover:bg-slate-50/80 text-sm text-slate-700">
                  <td className="px-4 py-3 font-medium text-slate-900">{c.lender_name}</td>
                  <td className="px-4 py-3">{c.loan_type}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{methodLabel(c.method)}</span></td>
                  <td className="px-4 py-3">{c.partner_name ? <span className="text-indigo-600">DSA: {c.partner_name}</span> : <span className="text-slate-400">All DSAs</span>}</td>
                  <td className="px-4 py-3 text-right font-mono font-semibold">{cfgValue(c)}</td>
                  <td className="px-4 py-3 text-right font-mono">{c.tds_percent == null ? `${globalTds}%` : `${c.tds_percent}%`}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {c.effective_from || c.effective_to
                      ? <span>{c.effective_from ? formatDate(c.effective_from) : "—"} <span className="text-slate-300">→</span> {c.effective_to ? formatDate(c.effective_to) : "∞"}</span>
                      : <span className="text-slate-400">Always</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`view-config-${c.id}`} title="View rule" onClick={() => setViewCfg(c)} className="p-2 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50"><Eye size={15} /></button>
                      {canEdit && <button data-testid={`edit-config-${c.id}`} title="Edit rule" onClick={() => openEditCfg(c)} className="p-2 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50"><Pencil size={15} /></button>}
                      {canEdit && <button data-testid={`delete-config-${c.id}`} title="Delete rule" onClick={() => delCfg(c.id)} className="p-2 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50"><Trash2 size={15} /></button>}
                    </div>
                  </td>
                </tr>
              ))}
              {configs.length === 0 && <tr><td colSpan={8} className="px-4 py-12 text-center text-slate-400 text-sm">No payout rules configured.</td></tr>}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <Modal open={showCfg} onClose={() => { setShowCfg(false); setEditCfgId(null); }} title={editCfgId ? "Edit Payout Rule" : "Add Payout Rule"} size="lg" testId="config-modal">
        <form onSubmit={submitCfg} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Lender *</label>
              <select data-testid="config-lender-select" required value={cfgForm.lender_id} onChange={(e) => setCfgForm({ ...cfgForm, lender_id: e.target.value })} className={inputCls}>
                <option value="">Select…</option>{lenders.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Loan Type *</label>
              <select data-testid="config-loan-type-select" value={cfgForm.loan_type} onChange={(e) => setCfgForm({ ...cfgForm, loan_type: e.target.value })} className={inputCls}>
                {products.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Payout Method *</label>
              <select data-testid="config-method-select" value={cfgForm.method} onChange={(e) => setCfgForm({ ...cfgForm, method: e.target.value })} className={inputCls}>
                {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>TDS % (optional)</label><input data-testid="config-tds-input" type="number" step="0.01" value={cfgForm.tds_percent} onChange={(e) => setCfgForm({ ...cfgForm, tds_percent: e.target.value })} className={inputCls} placeholder={`Default ${globalTds}%`} /></div>
            <div><label className={labelCls}>Effective From (optional)</label><input data-testid="config-effective-from" type="date" value={cfgForm.effective_from} onChange={(e) => setCfgForm({ ...cfgForm, effective_from: e.target.value })} className={inputCls} /></div>
            <div><label className={labelCls}>Effective To (optional)</label><input data-testid="config-effective-to" type="date" value={cfgForm.effective_to} onChange={(e) => setCfgForm({ ...cfgForm, effective_to: e.target.value })} className={inputCls} /></div>
            {cfgForm.method === "percent" && (
              <div><label className={labelCls}>Payout % *</label><input data-testid="config-payout-input" required type="number" step="0.01" value={cfgForm.payout_percent} onChange={(e) => setCfgForm({ ...cfgForm, payout_percent: e.target.value })} className={inputCls} /></div>
            )}
            {cfgForm.method === "amount" && (
              <div><label className={labelCls}>Payout Amount (₹) *</label><input data-testid="config-amount-input" required type="number" step="0.01" value={cfgForm.payout_amount} onChange={(e) => setCfgForm({ ...cfgForm, payout_amount: e.target.value })} className={inputCls} placeholder="Fixed ₹ per case" /></div>
            )}
            {isMatrix && (
              <div><label className={labelCls}>Default Payout % (fallback)</label><input data-testid="config-matrix-fallback-input" type="number" step="0.01" value={cfgForm.payout_percent} onChange={(e) => setCfgForm({ ...cfgForm, payout_percent: e.target.value })} className={inputCls} placeholder="Used when no slab matches" /></div>
            )}
          </div>

          {isMatrix && (
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">IRR % × Tenure (months) Slabs → Payout %</span>
                <button type="button" data-testid="config-add-slab-button" onClick={addSlab} className="text-xs text-emerald-600 font-medium">+ Add slab</button>
              </div>
              <div className="space-y-2">
                {(cfgForm.slabs || []).map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input data-testid={`config-slab-irr-min-${i}`} type="number" step="0.01" value={s.min_irr} onChange={(e) => updSlab(i, "min_irr", e.target.value)} placeholder="IRR min" className={`${inputCls} col-span-2`} />
                    <input data-testid={`config-slab-irr-max-${i}`} type="number" step="0.01" value={s.max_irr} onChange={(e) => updSlab(i, "max_irr", e.target.value)} placeholder="IRR max" className={`${inputCls} col-span-2`} />
                    <input data-testid={`config-slab-tenure-min-${i}`} type="number" value={s.min_tenure} onChange={(e) => updSlab(i, "min_tenure", e.target.value)} placeholder="Tenure min" className={`${inputCls} col-span-2`} />
                    <input data-testid={`config-slab-tenure-max-${i}`} type="number" value={s.max_tenure} onChange={(e) => updSlab(i, "max_tenure", e.target.value)} placeholder="Tenure max" className={`${inputCls} col-span-2`} />
                    <input data-testid={`config-slab-percent-${i}`} type="number" step="0.01" value={s.payout_percent} onChange={(e) => updSlab(i, "payout_percent", e.target.value === "" ? "" : Number(e.target.value))} placeholder="%" className={`${inputCls} col-span-3`} />
                    <button type="button" onClick={() => removeSlab(i)} className="col-span-1 p-2 rounded-lg text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button>
                  </div>
                ))}
                {(!cfgForm.slabs || cfgForm.slabs.length === 0) && <p className="text-xs text-slate-400">No slabs yet. First matching row wins; blank min/max is open-ended. If nothing matches, the Default Payout % applies.</p>}
              </div>
            </div>
          )}

          {isSlab && (
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{cfgForm.method === "volume_slab" ? "Disbursed Amount Slabs (₹)" : "Case-count Slabs"} → Payout %</span>
                <button type="button" data-testid="config-add-slab-button" onClick={addSlab} className="text-xs text-emerald-600 font-medium">+ Add slab</button>
              </div>
              <div className="space-y-2">
                {(cfgForm.slabs || []).map((s, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input data-testid={`config-slab-min-${i}`} type="number" value={s.min} onChange={(e) => updSlab(i, "min", e.target.value === "" ? "" : Number(e.target.value))} placeholder="Min" className={`${inputCls} col-span-4`} />
                    <input data-testid={`config-slab-max-${i}`} type="number" value={s.max} onChange={(e) => updSlab(i, "max", e.target.value === "" ? "" : Number(e.target.value))} placeholder="Max (blank=∞)" className={`${inputCls} col-span-4`} />
                    <input data-testid={`config-slab-percent-${i}`} type="number" step="0.01" value={s.payout_percent} onChange={(e) => updSlab(i, "payout_percent", e.target.value === "" ? "" : Number(e.target.value))} placeholder="%" className={`${inputCls} col-span-3`} />
                    <button type="button" onClick={() => removeSlab(i)} className="col-span-1 p-2 rounded-lg text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button>
                  </div>
                ))}
                {(!cfgForm.slabs || cfgForm.slabs.length === 0) && <p className="text-xs text-slate-400">No slabs yet. Add rows to define payout % per slab.</p>}
              </div>
            </div>
          )}

          <div><label className={labelCls}>DSA Override (optional)</label>
            <select data-testid="config-partner-select" value={cfgForm.partner_id} onChange={(e) => setCfgForm({ ...cfgForm, partner_id: e.target.value })} className={inputCls}>
              <option value="">Applies to all DSAs</option>{partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => { setShowCfg(false); setEditCfgId(null); }} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button data-testid="submit-config-button" type="submit" className={btnPrimary}>{editCfgId ? "Save Changes" : "Add Rule"}</button>
          </div>
        </form>
      </Modal>

      <Modal open={!!viewCfg} onClose={() => setViewCfg(null)} title="Payout Rule Details" testId="view-config-modal">
        {viewCfg && (
          <div className="space-y-3 text-sm">
            {[
              ["Lender", viewCfg.lender_name],
              ["Loan Type", viewCfg.loan_type],
              ["Method", methodLabel(viewCfg.method)],
              ["Scope", viewCfg.partner_name ? `DSA: ${viewCfg.partner_name}` : "All DSAs"],
              ["Payout", cfgValue(viewCfg)],
              ["TDS %", viewCfg.tds_percent == null ? `${globalTds}% (default)` : `${viewCfg.tds_percent}%`],
              ["Effective From", viewCfg.effective_from ? formatDate(viewCfg.effective_from) : "—"],
              ["Effective To", viewCfg.effective_to ? formatDate(viewCfg.effective_to) : "∞ (no end)"],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
                <span className="text-slate-500">{k}</span><span className="font-medium text-slate-800">{v || "—"}</span>
              </div>
            ))}
            {(viewCfg.method === "volume_slab" || viewCfg.method === "count_slab") && (viewCfg.slabs || []).length > 0 && (
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Slabs</div>
                <div className="space-y-1">
                  {viewCfg.slabs.map((s, i) => (
                    <div key={i} className="flex justify-between text-xs text-slate-600"><span>{s.min} – {s.max === "" || s.max == null ? "∞" : s.max}</span><span className="font-mono">{s.payout_percent}%</span></div>
                  ))}
                </div>
              </div>
            )}
            {viewCfg.method === "irr_tenure_matrix" && (
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50" data-testid="view-matrix-slabs">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">IRR/Tenure Slabs (fallback: {viewCfg.payout_percent ?? 0}%)</div>
                <div className="space-y-1">
                  {(viewCfg.slabs || []).map((s, i) => (
                    <div key={i} className="flex justify-between text-xs text-slate-600">
                      <span>IRR {s.min_irr === "" || s.min_irr == null ? "0" : s.min_irr}–{s.max_irr === "" || s.max_irr == null ? "∞" : s.max_irr}% · {s.min_tenure === "" || s.min_tenure == null ? "0" : s.min_tenure}–{s.max_tenure === "" || s.max_tenure == null ? "∞" : s.max_tenure} mo</span>
                      <span className="font-mono">{s.payout_percent}%</span>
                    </div>
                  ))}
                  {(viewCfg.slabs || []).length === 0 && <p className="text-xs text-slate-400">No slabs defined.</p>}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-3 pt-1">
              {canEdit && <button data-testid="view-to-edit-config" onClick={() => { openEditCfg(viewCfg); setViewCfg(null); }} className={btnPrimary}><Pencil size={15} /> Edit Rule</button>}
              <button onClick={() => setViewCfg(null)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Close</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!settleFor} onClose={() => setSettleFor(null)} title={settleFor?.status === "settled" ? "Edit Settlement" : "Settle Commission"} testId="settle-modal">
        <div className="space-y-4">
          <div className="text-sm text-slate-600">Calculated net <span className="font-semibold text-slate-800">{formatINR(settleFor?.net_payout)}</span> for <span className="font-semibold text-slate-800">{settleFor?.partner_name}</span> ({settleFor?.customer_name}).</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Amount Paid (₹)</label>
              <input data-testid="settle-paid-input" type="number" step="0.01" value={settlePaid} onChange={(e) => setSettlePaid(e.target.value)} className={inputCls} placeholder="Custom paid amount" />
              <p className="text-[11px] text-slate-400 mt-1">Defaults to net payout; change if a custom amount was paid.</p>
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select data-testid="settle-status-select" value={settleStatus} onChange={(e) => setSettleStatus(e.target.value)} className={inputCls}>
                <option value="settled">Settled</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>
          <div>
            <label className={labelCls}>Settlement Comment</label>
            <textarea data-testid="settle-note-input" rows={3} value={settleNote} onChange={(e) => setSettleNote(e.target.value)} className={inputCls} placeholder="e.g. Paid via NEFT ref# 12345 on 06 Jun" />
          </div>
          <div className="flex justify-end gap-3">
            <button onClick={() => setSettleFor(null)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button data-testid="confirm-settle-button" onClick={settle} className={btnPrimary}>{settleStatus === "settled" ? "Save Settlement" : "Save"}</button>
          </div>
        </div>
      </Modal>

      <Modal open={!!editValFor} onClose={() => setEditValFor(null)} title="Edit Calculated Values" testId="edit-values-modal">
        <div className="space-y-4">
          <div className="text-sm text-slate-600">Manually override the auto-calculated payout for <span className="font-semibold text-slate-800">{editValFor?.partner_name}</span> ({editValFor?.customer_name}). Disbursed <span className="font-mono text-slate-800">{formatINR(editValFor?.disbursed_amount)}</span>.</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Payout %</label>
              <input data-testid="edit-payout-percent" type="number" step="0.01" value={editVal.payout_percent} onChange={(e) => setEditVal({ ...editVal, payout_percent: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Incentive (₹)</label>
              <input data-testid="edit-incentive" type="number" step="0.01" value={editVal.incentive} onChange={(e) => setEditVal({ ...editVal, incentive: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>TDS %</label>
              <input data-testid="edit-tds-percent" type="number" step="0.01" value={editVal.tds_percent} onChange={(e) => setEditVal({ ...editVal, tds_percent: e.target.value })} className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 p-4 rounded-lg bg-slate-50 border border-slate-200 text-sm">
            <div><div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Gross</div><div className="font-mono font-semibold text-slate-800 mt-0.5" data-testid="preview-gross">{formatINR(previewNet().gross)}</div></div>
            <div><div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">TDS</div><div className="font-mono font-semibold text-rose-600 mt-0.5" data-testid="preview-tds">-{formatINR(previewNet().tds)}</div></div>
            <div><div className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">Net Payout</div><div className="font-mono font-semibold text-emerald-600 mt-0.5" data-testid="preview-net">{formatINR(previewNet().net)}</div></div>
          </div>
          <p className="text-[11px] text-slate-400">Gross = Disbursed × Payout% · TDS = (Gross + Incentive) × TDS% · Net = Gross + Incentive − TDS</p>
          <div className="flex justify-end gap-3 pt-1">
            <button onClick={() => setEditValFor(null)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button data-testid="confirm-edit-values" onClick={saveEditValues} className={btnPrimary}>Save Values</button>
          </div>
        </div>
      </Modal>

      <Modal open={showManual} onClose={() => setShowManual(false)} title="Manual / Additional Payout" testId="manual-modal">
        <form onSubmit={submitManual} className="space-y-4">
          <p className="text-sm text-slate-500">Create a standalone payout for a partner (incentive, bonus or adjustment) not tied to a specific lead.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Partner *</label>
              <select data-testid="manual-partner-select" required value={manualForm.partner_id} onChange={(e) => setManualForm({ ...manualForm, partner_id: e.target.value })} className={inputCls}>
                <option value="">Select…</option>{partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Amount (₹) *</label>
              <input data-testid="manual-amount-input" required type="number" step="0.01" value={manualForm.amount} onChange={(e) => setManualForm({ ...manualForm, amount: e.target.value })} className={inputCls} placeholder="0" />
            </div>
            <div><label className={labelCls}>Lender (optional)</label>
              <select data-testid="manual-lender-select" value={manualForm.lender_id} onChange={(e) => setManualForm({ ...manualForm, lender_id: e.target.value })} className={inputCls}>
                <option value="">—</option>{lenders.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Loan Type (optional)</label>
              <select data-testid="manual-loan-type-select" value={manualForm.loan_type} onChange={(e) => setManualForm({ ...manualForm, loan_type: e.target.value })} className={inputCls}>
                <option value="">—</option>{products.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </div>
          </div>
          <div><label className={labelCls}>Note / Reason</label>
            <input data-testid="manual-note-input" value={manualForm.note} onChange={(e) => setManualForm({ ...manualForm, note: e.target.value })} className={inputCls} placeholder="e.g. Quarterly incentive" />
          </div>
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={() => setShowManual(false)} className="px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button data-testid="submit-manual-button" type="submit" className={btnPrimary}>Add Payout</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
